import React from "react";
import { Link } from "react-router-dom";
import { useApp } from "../../store";
import { useHomeData, HeroVideo } from "../../homeData";
import { LearnSection, InstagramSection, BlogSection } from "./V1";

/* V5 — Editorial Magazine. Big typography, full-width photos, narrative. */
export default function V5() {
  const { t } = useApp();
  const { products, blog, igPosts } = useHomeData();

  return (
    <main className="bg-[#FAFAF7]" data-testid="home-v5">
      {/* HERO - magazine style */}
      <section className="relative h-screen overflow-hidden">
        <HeroVideo overlay="linear-gradient(0deg, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0.0) 60%)" />
        <div className="absolute inset-0 grid place-items-center">
          <div className="text-center px-6 fade-up">
            <div className="overline mb-6 text-white drop-shadow">VOL · 01 — 2026</div>
            <h1 className="font-serif-display text-7xl md:text-[10rem] lg:text-[14rem] leading-[0.85] text-white drop-shadow-lg max-w-6xl">{t.hero.title}</h1>
          </div>
        </div>
        <div className="absolute bottom-12 left-0 right-0 px-6 md:px-12">
          <div className="max-w-[1440px] mx-auto flex items-end justify-between gap-6">
            <div className="text-slate-900 max-w-md">
              <div className="overline mb-2">{t.hero.overline}</div>
              <p className="font-serif-display text-2xl italic">{t.hero.subtitle}</p>
            </div>
            <Link to="/products/efoils" className="btn-primary">{t.hero.cta1}</Link>
          </div>
        </div>
      </section>

      {/* Editorial USPs */}
      <section className="py-28 px-6 md:px-12 max-w-[1100px] mx-auto">
        <div className="overline mb-3">Feature · Engineering</div>
        <h2 className="font-serif-display text-6xl md:text-8xl leading-[0.95] text-slate-900">{t.usps.title}</h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mt-16">
          <p className="md:col-span-5 font-serif-display text-2xl italic text-slate-700 leading-snug">"Every kilogram you cut from a hydrofoil board translates directly into agility, range, and joy."</p>
          <p className="md:col-span-7 text-slate-700 leading-relaxed text-lg">
            We obsessed over every gram. Aerospace-grade carbon. An integrated battery tray. A motor designed from scratch — not repurposed. The result is the world's lightest production e-foil at <span className="text-sky-600 font-semibold">24 kilograms</span>, eleven kilograms below the closest competitor with identical battery duration. Lighter board. Faster planing. Longer rides. Easier transport.
          </p>
        </div>

        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6">
          <BigStat n="24" u="kg" label="World's lightest" />
          <BigStat n="11" u="kg" label="Saved vs comp." />
          <BigStat n="6" u="s" label="Battery swap" />
          <BigStat n="0" u="" label="Tools required" />
        </div>
      </section>

      {/* Full-width image break */}
      <section className="relative h-[70vh] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1769816325482-59769ff9ab37?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
        <div className="absolute bottom-12 left-0 right-0 px-6 md:px-12">
          <div className="max-w-[1440px] mx-auto">
            <div className="overline text-cyan-300 mb-2">Field Report</div>
            <div className="font-serif-display text-4xl md:text-6xl text-white max-w-3xl">"Forty-five minutes from beach to flight."</div>
          </div>
        </div>
      </section>

      {/* Product editorial */}
      <section className="py-28 px-6 md:px-12 max-w-[1440px] mx-auto">
        <div className="overline mb-3">The collection</div>
        <h2 className="font-serif-display text-5xl md:text-7xl text-slate-900 mb-16">{t.shop.title}</h2>
        <div className="space-y-24">
          {products.slice(0, 3).map((p, i) => (
            <article key={p.id} className={`grid grid-cols-1 md:grid-cols-12 gap-10 items-center ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}>
              <div className="md:col-span-7 aspect-[16/11] overflow-hidden rounded-md">
                <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="md:col-span-5">
                <div className="overline">{p.category} · No. {String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-serif-display text-5xl md:text-6xl mt-4 text-slate-900">{p.name}</h3>
                <p className="font-serif-display italic text-xl text-slate-600 mt-4">"{p.tagline}"</p>
                <p className="text-slate-700 mt-6 leading-relaxed">{p.description?.slice(0, 220) || ""}</p>
                <Link to={`/product/${p.slug}`} className="btn-primary mt-10">Read more</Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      <LearnSection t={t} />
      <InstagramSection posts={igPosts} />
      <BlogSection blog={blog} />
    </main>
  );
}

function BigStat({ n, u, label }) {
  return (
    <div className="border-t border-slate-300 pt-4">
      <div className="font-serif-display text-6xl text-slate-900">{n}<span className="text-sky-500 text-3xl ml-1">{u}</span></div>
      <div className="text-xs uppercase tracking-widest text-slate-500 mt-2 font-ui">{label}</div>
    </div>
  );
}
